import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase.ts';
import { callApi } from '../lib/api.ts';
import { deriveSlug, sanitizeSlug } from '../lib/slug.ts';
import { Toast, type ToastState } from './Toast.tsx';
import { StatusChip } from './StatusChip.tsx';
import { Spinner } from './BuildStatus.tsx';
import { Icon } from './Icon.tsx';
import { btnSolid, btnSolidBadge, btnGhost, input as inputCls, card, sectionTitle, sectionDesc } from '../lib/ui.ts';
import { FlapText } from './FlapText.tsx';
import { goLiveItems, goLiveProgress, goLiveComplete, type ClientType } from '../lib/golive.ts';
import { recentCompleteQuarters } from '../lib/quarters.ts';
import { isMaintainableSlug } from '../lib/liveMaintain.ts';
import type { Assignment, Client, Profile } from '../lib/types.ts';
import { friendlyError } from '../lib/friendlyError.ts';

// Timezone dropdown (analytics config) — a mistyped IANA zone silently skews report period
// boundaries, so the field is a <select> of real zones. Prefer the full runtime IANA list
// (Intl.supportedValuesOf); fall back to a curated common set on the rare browser without it.
const COMMON_TZ = [
  'UTC',
  'America/Toronto', 'America/New_York', 'America/Chicago', 'America/Denver',
  'America/Los_Angeles', 'America/Vancouver', 'America/Sao_Paulo',
  'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Madrid',
  'Africa/Johannesburg', 'Asia/Dubai', 'Asia/Kolkata', 'Asia/Singapore',
  'Asia/Tokyo', 'Australia/Sydney', 'Pacific/Auckland',
];
/** The IANA zone list for the select, with the client's CURRENT stored value guaranteed present
 *  (prepended if unknown) — so a legacy/curated-list-miss value stays selected and is NEVER
 *  silently dropped/clobbered on save. */
function tzOptions(current: string): string[] {
  let zones: string[] = [];
  try {
    zones = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.('timeZone') ?? [];
  } catch {
    zones = [];
  }
  if (zones.length === 0) zones = COMMON_TZ;
  if (current && !zones.includes(current)) zones = [current, ...zones];
  return zones;
}

// RT-2b: curated DataForSEO `location_name` suggestions for the rank-tracker Location field. A SERP
// position is location-specific, and a free-text mistype silently skews a check, so the field is a
// datalist (dropdown suggestions) — click a listed market, or type an exact location_name for one
// not listed (the EXACT string is stored either way). These are the standard DataForSEO forms
// (country, or City,Region,Country); ① can extend from /v3/serp/google/locations as clients arrive.
const DFS_LOCATIONS = [
  'United Kingdom', 'London,England,United Kingdom', 'Manchester,England,United Kingdom',
  'Canada', 'Toronto,Ontario,Canada', 'Vancouver,British Columbia,Canada', 'Montreal,Quebec,Canada',
  'United States', 'New York,New York,United States', 'Los Angeles,California,United States',
  'Australia', 'Sydney,New South Wales,Australia', 'Melbourne,Victoria,Australia',
  'Ireland', 'New Zealand',
];

/**
 * Admin client management: create (persisted: repo seed + DB row), edit basic
 * fields (name, Pages project, status), and assign/unassign designers. Designer↔
 * client scoping is enforced by RLS — assignments here are what designers then see.
 * Framed around the logical setup flow: Create → Pages project → Assign.
 */
export function ClientsManager({ clients, onChange }: { clients: Client[]; onChange: () => void }) {
  const [designers, setDesigners] = useState<Profile[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [tick, setTick] = useState(0);

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [project, setProject] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [projectTouched, setProjectTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);

  useEffect(() => {
    void Promise.all([
      supabase.from('profiles').select('*').eq('role', 'designer').order('email'),
      supabase.from('assignments').select('*'),
    ]).then(([d, a]) => {
      if (!d.error) setDesigners((d.data ?? []) as Profile[]);
      if (!a.error) setAssignments((a.data ?? []) as Assignment[]);
    });
  }, [tick, clients.length]);

  function refresh() {
    onChange();
    setTick((t) => t + 1);
  }

  // Auto-derive slug + pages_project from the Name (convention: CF project = slug),
  // both still editable. "touched" tracking stops manual edits being overwritten.
  function onName(v: string) {
    if (msg) setMsg(null); // clear the success state once a new client is started
    setName(v);
    if (!slugTouched) {
      const s = deriveSlug(v);
      setSlug(s);
      if (!projectTouched) setProject(s);
    }
  }
  function onSlug(v: string) {
    const s = sanitizeSlug(v);
    setSlug(s);
    setSlugTouched(true);
    if (!projectTouched) setProject(s);
  }
  function onProject(v: string) {
    setProject(sanitizeSlug(v));
    setProjectTouched(true);
  }

  async function create() {
    setErr(null);
    setMsg(null);
    if (!name.trim()) return setErr('Name is required.');
    if (!/^[a-z0-9][a-z0-9-]*$/.test(slug.trim().toLowerCase()))
      return setErr('Slug must be lowercase alphanumeric/hyphen.');
    setBusy(true);
    try {
      const out = await callApi<{ client: Client; note: string; provisionBuildId: string | null }>('create-client', {
        method: 'POST',
        body: JSON.stringify({ name: name.trim(), slug: slug.trim().toLowerCase(), pages_project: project.trim() }),
      });
      setName('');
      setSlug('');
      setProject('');
      setSlugTouched(false);
      setProjectTouched(false);
      refresh();

      // Auto-regen (create-client-onboarding.md #1) runs in the background — poll it
      // so the admin sees "provisioning → ready" instead of a silent manual step.
      if (out.provisionBuildId) {
        setMsg(`${out.client.name} created — provisioning the site branch (reconciling the lockfile)…`);
        const started = Date.now();
        // BUILD-QUALITY #1: poll past the backend's honest-failure ceiling (17 min) so a
        // slow-but-healthy provision (regen up to 12 min + CF create) is observed flipping
        // to ready, not abandoned mid-flight by a too-short client budget.
        while (Date.now() - started < 19 * 60 * 1000) {
          await new Promise((r) => setTimeout(r, 5_000));
          let st: { status: string; error?: string | null; cfProject?: string | null; cfRepo?: string | null };
          try {
            st = await callApi('create-client-status', {
              method: 'POST',
              body: JSON.stringify({ buildId: out.provisionBuildId }),
            });
          } catch {
            continue; // transient — keep polling
          }
          if (st.status === 'ready') {
            setToast({ message: `${out.client.name} provisioned and ready.` });
            if (st.cfProject === 'created' || st.cfProject === 'existing') {
              setMsg(
                `${out.client.name} ready — site branch provisioned and its Cloudflare Pages project is set up. ` +
                  `Assign a designer and build; go live when the preview is green.`,
              );
            } else {
              // Fallback A — the API couldn't git-connect; guide the manual CF step.
              setMsg(
                `${out.client.name} ready (lockfile reconciled). Last step — create its Cloudflare Pages project manually: ` +
                  `Connect to Git → repo ${st.cfRepo ?? 'alchmystudio/alchmy-studio'} · build "pnpm turbo build --filter=${out.client.slug}" · ` +
                  `output sites/${out.client.slug}/dist · root / · Node 20 · env NPM_TOKEN. Then go live.`,
              );
            }
            break;
          }
          if (st.status === 'failed') {
            // The CLIENT was created + the branch seeded regardless — only the auto
            // lockfile-regen didn't finish. Say so, so a slow/failed regen never reads
            // as "the client wasn't created" (BUILD-QUALITY #1).
            setErr(
              `${out.client.name} was created (site branch app/${out.client.slug} is seeded), but the automatic ` +
                `lockfile-regen didn't finish (${st.error ?? 'unknown'}). Run Actions → Regen-lockfile on ` +
                `app/${out.client.slug} to complete provisioning — no need to recreate the client.`,
            );
            break;
          }
        }
        // Budget elapsed while still provisioning: the worker/reconcile carries on in
        // the background — this is NOT a failure. Point the admin at the live list.
        if (Date.now() - started >= 19 * 60 * 1000) {
          setMsg(
            `${out.client.name} created — still provisioning in the background. It'll show as ready in the ` +
              `clients list shortly; no need to recreate it.`,
          );
        }
      } else {
        setMsg(`${out.client.name} created`);
        setToast({ message: `${out.client.name} created.` });
      }
    } catch (e) {
      setErr(friendlyError(e, (e as Error).message));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className={`${card} space-y-5 p-5`}>
      <Toast toast={toast} onClose={() => setToast(null)} />
      <div>
        <h2 className={sectionTitle}>Clients ({clients.length})</h2>
        <p className={sectionDesc}>
          Admin only. Setup flow: <b>Create</b> → operator steps (Cloudflare Pages project +
          pnpm lockfile, see client-site-deploy.md) → set the <b>Pages project</b> → <b>Assign
          designers</b> → designer builds in the wizard (Brand kit → Review → Go live).
        </p>
      </div>

      {/* 6b-4a: fleet-level report copy-me toggle (§14.8) — BCC Alchmy on every client report while
          proving the rubric; flip off once trusted. */}
      <ReportCopySettings />

      {/* Create */}
      <div className="flex flex-wrap items-end gap-2 rounded border border-line bg-paper-2 p-3">
        <Field label="Name">
          <input value={name} onChange={(e) => onName(e.target.value)} placeholder="Acme Co" className={inputCls} />
        </Field>
        <Field label="Slug (auto)">
          <input value={slug} onChange={(e) => onSlug(e.target.value)} placeholder="acme" spellCheck={false}
            className={`${inputCls} font-mono`} />
        </Field>
        <Field label="Pages project (auto)">
          <input value={project} onChange={(e) => onProject(e.target.value)} placeholder="acme" spellCheck={false}
            className={`${inputCls} font-mono`} />
        </Field>
        <button onClick={create} disabled={busy} className={btnSolid}>
          {busy ? (
            <FlapText>Creating…</FlapText>
          ) : msg ? (
            <>
              <Icon name="check" title="done" /> <FlapText>Client created</FlapText>
            </>
          ) : (
            <FlapText>Create client</FlapText>
          )}
        </button>
        {msg && <span className="text-sm font-medium text-success">{msg}</span>}
      </div>
      {err && <p className="text-sm text-danger">{err}</p>}

      {/* List — collapsible to keep the long roster tidy (open by default). */}
      {clients.length === 0 ? (
        <p className="text-sm text-ink-2">No clients yet.</p>
      ) : (
        <details open className="group">
          <summary className="cursor-pointer list-none text-xs font-medium text-ink-2 hover:text-ink">
            <span className="group-open:hidden">▸ Show clients ({clients.length})</span>
            <span className="hidden group-open:inline">▾ Hide clients</span>
          </summary>
          <div className="mt-2 space-y-3">
            {clients.map((c) => (
              <ClientRow
                key={c.id}
                client={c}
                designers={designers}
                assignments={assignments.filter((a) => a.client_id === c.id)}
                onChanged={refresh}
              />
            ))}
          </div>
        </details>
      )}
    </section>
  );
}

function ClientRow({
  client,
  designers,
  assignments,
  onChanged,
}: {
  client: Client;
  designers: Profile[];
  assignments: Assignment[];
  onChanged: () => void;
}) {
  const [name, setName] = useState(client.name);
  const [project, setProject] = useState(client.pages_project ?? '');
  const [status, setStatus] = useState(client.status);
  const [isLive, setIsLive] = useState(client.is_live);
  const [addId, setAddId] = useState('');
  const [busy, setBusy] = useState(false);

  // 6b-0: the per-client analytics MANIFEST (client_analytics) — lazy-loaded when the
  // panel opens; written through the admin service_role endpoint (browser RLS is read-only).
  const [aOpen, setAOpen] = useState(false);
  const [aLoaded, setALoaded] = useState(false);
  const [aBusy, setABusy] = useState(false);
  const [aMsg, setAMsg] = useState<string | null>(null);
  const [plausible, setPlausible] = useState('');
  const [gsc, setGsc] = useState('');
  const [cfRef, setCfRef] = useState('');
  const [tz, setTz] = useState('');
  // The zone list always includes the current stored tz (see tzOptions) so binding value={tz}
  // keeps it selected — never a silent clobber (e.g. Cooper's America/Toronto).
  const tzList = useMemo(() => tzOptions(tz), [tz]);
  const [aEnabled, setAEnabled] = useState(false);
  const [recipient, setRecipient] = useState(''); // 6b-4: where the quarterly report is emailed
  const [clientType, setClientType] = useState<ClientType>(null); // 6a-2: video | photo
  const [golive, setGolive] = useState<Record<string, boolean>>({}); // 6a-2: checklist state

  // RT-1: the per-client rank-tracker CONFIG (client_keywords) — same lazy-load/service_role-write
  // pattern as the analytics manifest above. Target keyword set + the location/language a SERP
  // position is measured at (mandatory to enter the monthly cron).
  const [kOpen, setKOpen] = useState(false);
  const [kLoaded, setKLoaded] = useState(false);
  const [kBusy, setKBusy] = useState(false);
  const [kMsg, setKMsg] = useState<string | null>(null);
  const [kwText, setKwText] = useState(''); // textarea: one keyword per line (or comma-separated)
  const [kLocation, setKLocation] = useState('');
  const [kLanguage, setKLanguage] = useState('English'); // RT-2b: default English (editable)
  const [kEnabled, setKEnabled] = useState(false);

  type ACfg = { plausibleDomain: string | null; gscProperty: string | null; cfRef: string | null; timezone: string | null; enabled: boolean; reportRecipient: string | null; clientType: ClientType; golive: Record<string, boolean> };
  async function loadAnalytics() {
    setABusy(true);
    setAMsg(null);
    try {
      const r = await callApi<{ config: ACfg }>('analytics-config', {
        method: 'POST',
        body: JSON.stringify({ clientId: client.id, get: true }),
      });
      setPlausible(r.config.plausibleDomain ?? '');
      setGsc(r.config.gscProperty ?? '');
      setCfRef(r.config.cfRef ?? '');
      setTz(r.config.timezone ?? '');
      setAEnabled(r.config.enabled);
      setRecipient(r.config.reportRecipient ?? '');
      setClientType(r.config.clientType ?? null);
      setGolive(r.config.golive ?? {});
      setALoaded(true);
    } catch (e) {
      setAMsg(friendlyError(e, (e as Error).message));
    } finally {
      setABusy(false);
    }
  }
  async function saveAnalytics() {
    setABusy(true);
    setAMsg(null);
    try {
      await callApi('analytics-config', {
        method: 'POST',
        body: JSON.stringify({ clientId: client.id, config: { plausibleDomain: plausible, gscProperty: gsc, cfRef, timezone: tz, enabled: aEnabled, reportRecipient: recipient, clientType, golive } }),
      });
      await loadAnalytics(); // reflect the server-normalized values
      setAMsg('Saved.');
    } catch (e) {
      setAMsg(friendlyError(e, (e as Error).message));
    } finally {
      setABusy(false);
    }
  }

  // RT-1: rank-tracker config load/save (keywords-config endpoint; browser RLS is read-only).
  type KCfg = { keywords: string[]; location: string | null; language: string | null; enabled: boolean };
  async function loadKeywords() {
    setKBusy(true);
    setKMsg(null);
    try {
      const r = await callApi<{ config: KCfg }>('keywords-config', {
        method: 'POST',
        body: JSON.stringify({ clientId: client.id, get: true }),
      });
      setKwText((r.config.keywords ?? []).join('\n'));
      setKLocation(r.config.location ?? '');
      setKLanguage(r.config.language ?? 'English'); // RT-2b: pre-fill English for a fresh client
      setKEnabled(r.config.enabled);
      setKLoaded(true);
    } catch (e) {
      setKMsg(friendlyError(e, (e as Error).message));
    } finally {
      setKBusy(false);
    }
  }
  async function saveKeywords() {
    setKBusy(true);
    setKMsg(null);
    try {
      // keywords sent as the raw textarea string — the endpoint splits/dedupes/caps it.
      await callApi('keywords-config', {
        method: 'POST',
        body: JSON.stringify({ clientId: client.id, config: { keywords: kwText, location: kLocation, language: kLanguage, enabled: kEnabled } }),
      });
      await loadKeywords(); // reflect the server-normalized values
      setKMsg('Saved.');
    } catch (e) {
      setKMsg(friendlyError(e, (e as Error).message));
    } finally {
      setKBusy(false);
    }
  }

  // 6b-1 verify view: run Stage-1 PULL for a period (default last full month) + show the
  // returned snapshot. Internal — validates the real API integrations against Cooper (§14.7).
  type SourceStatus = { source: string; status: string; note?: string };
  type Snapshot = { sources?: SourceStatus[] } & Record<string, unknown>;
  // Report period is a dropdown of the last complete quarters (never a free-text field, so
  // there's nothing to mistype and no blank that silently defaults to "last quarter"). It's
  // preselected to the most-recent complete quarter, so every report handler always sends an
  // explicit `period`. Computed once from "now".
  const quarterOptions = useMemo(() => recentCompleteQuarters(new Date()), []);
  const [pullPeriod, setPullPeriod] = useState(() => quarterOptions[0]?.value ?? '');
  const [pullBusy, setPullBusy] = useState(false);
  const [pullErr, setPullErr] = useState<string | null>(null);
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  async function runPull() {
    setPullBusy(true);
    setPullErr(null);
    setSnapshot(null);
    try {
      // Async: the Stage-1 crawl (serial Browser Rendering, ~24s) exceeds the sync window, so kick
      // the background worker then poll until the snapshot is written — mirrors the interpret starter.
      const kick = await callApi<{ buildId: string; period: string }>('analytics-pull', {
        method: 'POST',
        body: JSON.stringify({ clientId: client.id, ...(pullPeriod.trim() ? { period: pullPeriod.trim() } : {}) }),
      });
      const started = Date.now();
      while (Date.now() - started < 15 * 60 * 1000) {
        await new Promise((r) => setTimeout(r, 5000));
        let st: { status: string; snapshot?: Snapshot | null; error?: string | null };
        try {
          st = await callApi('analytics-pull-status', {
            method: 'POST',
            body: JSON.stringify({ buildId: kick.buildId, period: kick.period }),
          });
        } catch {
          continue; // transient — keep polling
        }
        if (st.status === 'success') {
          setSnapshot(st.snapshot ?? null);
          return;
        }
        if (st.status === 'failed') {
          setPullErr(st.error ?? 'pull failed');
          return;
        }
      }
      setPullErr('pull timed out — check back shortly.');
    } catch (e) {
      setPullErr(friendlyError(e, (e as Error).message));
    } finally {
      setPullBusy(false);
    }
  }
  const sourceTone = (s: string) => (s === 'ok' ? 'success' : s === 'failed' ? 'danger' : 'neutral');

  // 6b-2 verify view: run Stage-2 INTERPRET (reads the pulled snapshot) + show the advisory
  // narrative readably, so we can eyeball quality against the skill's bar (§15).
  type NSection = { key: string; heading: string; metric: string; whatItMeans?: string; worthWatching?: string };
  type Narrative = { headline: string; summary: string; sections: NSection[]; dataNotes: string[]; nextFocus: string };
  const [interpBusy, setInterpBusy] = useState(false);
  const [interpErr, setInterpErr] = useState<string | null>(null);
  const [narrative, setNarrative] = useState<Narrative | null>(null);
  async function runInterpret() {
    setInterpBusy(true);
    setInterpErr(null);
    setNarrative(null);
    try {
      // Async: kick off the background worker (the Opus generation exceeds the sync window),
      // then poll for the narrative — mirrors the brand-extract starter+status pattern.
      const kick = await callApi<{ buildId: string; period: string }>('analytics-interpret', {
        method: 'POST',
        body: JSON.stringify({ clientId: client.id, ...(pullPeriod.trim() ? { period: pullPeriod.trim() } : {}) }),
      });
      const started = Date.now();
      while (Date.now() - started < 15 * 60 * 1000) {
        await new Promise((r) => setTimeout(r, 5000));
        let st: { status: string; narrative?: Narrative | null; error?: string | null };
        try {
          st = await callApi('analytics-interpret-status', {
            method: 'POST',
            body: JSON.stringify({ buildId: kick.buildId, period: kick.period }),
          });
        } catch {
          continue; // transient — keep polling
        }
        if (st.status === 'success') {
          setNarrative(st.narrative ?? null);
          return;
        }
        if (st.status === 'failed') {
          setInterpErr(st.error ?? 'interpret failed');
          return;
        }
      }
      setInterpErr('interpret timed out — check back shortly.');
    } catch (e) {
      setInterpErr(friendlyError(e, (e as Error).message));
    } finally {
      setInterpBusy(false);
    }
  }

  // 6b-3 verify: assemble + preview the branded HTML report (deterministic render of the
  // stored narrative; no send — that's 6b-4). Rendered in a sandboxed iframe so the email
  // styles are isolated from the app.
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewErr, setPreviewErr] = useState<string | null>(null);
  const [reportHtml, setReportHtml] = useState<string | null>(null);
  async function previewReport() {
    setPreviewBusy(true);
    setPreviewErr(null);
    setReportHtml(null);
    try {
      const r = await callApi<{ html: string }>('analytics-report-preview', {
        method: 'POST',
        body: JSON.stringify({ clientId: client.id, ...(pullPeriod.trim() ? { period: pullPeriod.trim() } : {}) }),
      });
      setReportHtml(r.html);
    } catch (e) {
      setPreviewErr(friendlyError(e, (e as Error).message));
    } finally {
      setPreviewBusy(false);
    }
  }

  // 6b-4a: the DELIVER actions (the review gate) — Approve (draft→approved) + Approve & send
  // (render → Resend → sent). Uses the same period as the pull/interpret/preview controls above.
  const [deliverBusy, setDeliverBusy] = useState<null | 'approve' | 'send'>(null);
  const [deliverErr, setDeliverErr] = useState<string | null>(null);
  const [deliverMsg, setDeliverMsg] = useState<string | null>(null);
  async function deliver(action: 'approve' | 'send') {
    setDeliverBusy(action);
    setDeliverErr(null);
    setDeliverMsg(null);
    try {
      const r = await callApi<{ status: string; recipient?: string; sentAt?: string }>('analytics-report-send', {
        method: 'POST',
        body: JSON.stringify({ clientId: client.id, action, ...(pullPeriod.trim() ? { period: pullPeriod.trim() } : {}) }),
      });
      setDeliverMsg(action === 'send' ? `Sent to ${r.recipient ?? 'the client'}.` : 'Approved — ready to send.');
    } catch (e) {
      setDeliverErr(friendlyError(e, (e as Error).message));
    } finally {
      setDeliverBusy(null);
    }
  }

  // Q3-regenerate affordance: reset an approved/sent report back to draft so a fresh pull → interpret
  // can regenerate it (the sanctioned way past the re-pull guard — e.g. the real end-of-quarter
  // report after a baseline test-send). Does NOT un-send email already delivered.
  const [resetBusy, setResetBusy] = useState(false);
  async function regenerate() {
    if (!window.confirm('Reset this report to draft? If it was approved or already sent, this re-opens it so you can pull fresh data and regenerate it. It does NOT recall any email already delivered.')) return;
    setResetBusy(true);
    setDeliverErr(null);
    setDeliverMsg(null);
    try {
      const r = await callApi<{ status: string; previous?: string }>('analytics-report-send', {
        method: 'POST',
        body: JSON.stringify({ clientId: client.id, action: 'reset', ...(pullPeriod.trim() ? { period: pullPeriod.trim() } : {}) }),
      });
      setDeliverMsg(
        r.previous && r.previous !== 'draft'
          ? `Reset from ${r.previous} → draft. Run pull → interpret to regenerate, then Approve & send.`
          : 'Already a draft — nothing to reset.',
      );
    } catch (e) {
      setDeliverErr(friendlyError(e, (e as Error).message));
    } finally {
      setResetBusy(false);
    }
  }

  const dirty =
    name !== client.name ||
    (project || null) !== client.pages_project ||
    status !== client.status ||
    isLive !== client.is_live;
  const assigned = assignments
    .map((a) => ({ assignmentId: a.id, designer: designers.find((d) => d.id === a.designer_id) }))
    .filter((x) => x.designer);
  const unassigned = designers.filter((d) => !assignments.some((a) => a.designer_id === d.id));

  async function save() {
    setBusy(true);
    await supabase
      .from('clients')
      .update({ name: name.trim(), pages_project: project.trim() || null, status, is_live: isLive })
      .eq('id', client.id);
    setBusy(false);
    onChanged();
  }
  async function assign() {
    if (!addId) return;
    setBusy(true);
    await supabase.from('assignments').insert({ client_id: client.id, designer_id: addId });
    setAddId('');
    setBusy(false);
    onChanged();
  }
  async function unassign(assignmentId: string) {
    setBusy(true);
    await supabase.from('assignments').delete().eq('id', assignmentId);
    setBusy(false);
    onChanged();
  }

  return (
    <div className="rounded border border-line p-3">
      <div className="flex flex-wrap items-end gap-2">
        <Field label="Name">
          <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
        </Field>
        <div className="text-xs text-ink-2">
          /{client.slug} · {client.repo_branch}
        </div>
        <Field label="Pages project">
          <input value={project} onChange={(e) => setProject(e.target.value)} placeholder="(set after CF project)"
            spellCheck={false} className={`${inputCls} font-mono`} />
        </Field>
        <Field label="Status">
          <select value={status} onChange={(e) => setStatus(e.target.value)} className={inputCls}>
            <option value="active">active</option>
            <option value="paused">paused</option>
          </select>
        </Field>
        <label className="flex items-center gap-1 text-xs text-ink-2" title="Block all app changes (build & go-live) to this client">
          <input type="checkbox" checked={isLive} onChange={(e) => setIsLive(e.target.checked)} />
          Live / protected
        </label>
        <button onClick={save} disabled={busy || !dirty} className={btnGhost}>
          Save
        </button>
      </div>

      {/* Setup checklist (guided steps) */}
      <div className="mt-2 flex flex-wrap gap-2 text-xs">
        <Pill ok><Icon name="check" title="done" /> Folder {client.repo_branch}</Pill>
        <Pill ok={!!client.pages_project}>
          {client.pages_project ? <><Icon name="check" title="done" /> Pages project {client.pages_project}</> : <><Icon name="warning" title="to do" /> Pages project — set after creating CF project</>}
        </Pill>
        <Pill ok={assigned.length > 0}>
          {assigned.length > 0 ? `${assigned.length} designer${assigned.length > 1 ? 's' : ''} assigned` : <><Icon name="warning" title="to do" /> no designers assigned</>}
        </Pill>
        {/* go-live-protection §3a: ClientsManager is admin-only (AdminDashboard), so canMaintainLive
            here reduces to isMaintainableSlug. A maintainable live client is MAINTENANCE MODE (admin
            edits preview; the live site changes only on a deliberate publish) — not "writes blocked";
            a protected live slug (james-cooper) truly is blocked for everyone. */}
        {client.is_live &&
          (isMaintainableSlug(client.slug) ? (
            <StatusChip tone="warning"><Icon name="lock" title="live" /> Live — maintenance mode</StatusChip>
          ) : (
            <StatusChip tone="danger"><Icon name="lock" title="live" /> Live — writes blocked</StatusChip>
          ))}
      </div>

      {/* Assignments */}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <span className="text-xs text-ink-2">Designers:</span>
        {assigned.map((x) => (
          <span key={x.assignmentId} className="inline-flex items-center gap-1 rounded border border-line bg-paper-2 px-2 py-0.5 text-xs">
            {x.designer!.email}
            <button onClick={() => unassign(x.assignmentId)} disabled={busy}
              className="text-ink-2 hover:text-danger" title="Unassign">×</button>
          </span>
        ))}
        {assigned.length === 0 && <span className="text-xs text-ink-2">none</span>}
        {unassigned.length > 0 && (
          <span className="inline-flex items-center gap-1">
            <select value={addId} onChange={(e) => setAddId(e.target.value)} className={`${inputCls} py-1`}>
              <option value="">add designer…</option>
              {unassigned.map((d) => (
                <option key={d.id} value={d.id}>{d.email}</option>
              ))}
            </select>
            <button onClick={assign} disabled={busy || !addId} className={btnGhost}>
              Assign
            </button>
          </span>
        )}
      </div>

      {/* 6b-0: analytics manifest — which sources the quarterly report pulls (§14.6). Interim
          manual entry until 6a auto-populates the same table. Lazy-loads on first open. */}
      <details
        className="mt-2 rounded border border-line"
        onToggle={(e) => {
          const open = (e.currentTarget as HTMLDetailsElement).open;
          setAOpen(open);
          if (open && !aLoaded && !aBusy) void loadAnalytics();
        }}
      >
        <summary className="cursor-pointer px-2 py-1 text-xs text-ink-2">Analytics config (reporting sources)</summary>
        {aOpen && (
          <div className="space-y-2 p-2 pt-1">
            {!aLoaded && aBusy ? (
              <p className="text-xs text-ink-2">Loading…</p>
            ) : (
              <>
                {/* Phase 5 item 1 — REPORTING: the quarterly-report pipeline, hoisted to the top of
                    the panel so opening a client's analytics lands on the report actions first
                    (the sources form + go-live checklist follow below). Item 2 — the actions read as
                    a numbered 1→5 stepper with Approve & send as the primary; handlers unchanged. */}
                <div className="mt-2 space-y-2 border-b border-line pb-3">
                  <div className="flex items-baseline gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-ink">Reporting</span>
                    <span className="text-[11px] text-ink-2">the quarterly client report</span>
                  </div>
                  <div className="flex flex-wrap items-end gap-2">
                    <Field label="Report period">
                      <select value={pullPeriod} onChange={(e) => setPullPeriod(e.target.value)} className={`${inputCls} font-mono`}>
                        {quarterOptions.map((q) => (
                          <option key={q.value} value={q.value}>{q.label}</option>
                        ))}
                      </select>
                    </Field>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button onClick={runPull} disabled={pullBusy} className={btnGhost} title="Pull the analytics snapshot from the sources — start here.">
                      {pullBusy ? <Spinner /> : <StepNum n={1} />}{pullBusy ? 'Pulling…' : 'Pull'}
                    </button>
                    <button onClick={runInterpret} disabled={interpBusy} className={btnGhost} title="Interprets the pulled snapshot into the advisory narrative (run the pull first).">
                      {interpBusy ? <Spinner /> : <StepNum n={2} />}{interpBusy ? 'Interpreting…' : 'Interpret'}
                    </button>
                    <button onClick={previewReport} disabled={previewBusy} className={btnGhost} title="Render the branded HTML report from the stored narrative (run interpret first). No send.">
                      {previewBusy ? <Spinner /> : <StepNum n={3} />}{previewBusy ? 'Rendering…' : 'Preview'}
                    </button>
                    <button onClick={() => deliver('approve')} disabled={deliverBusy !== null} className={btnGhost} title="Mark this report approved (draft → approved). No send.">
                      {deliverBusy === 'approve' ? <Spinner /> : <StepNum n={4} />}{deliverBusy === 'approve' ? 'Approving…' : 'Approve'}
                    </button>
                    <button onClick={() => deliver('send')} disabled={deliverBusy !== null} className={btnSolidBadge} title="Render + email the report to the client's recipient via Resend (BCCs the copy address when the fleet copy-me toggle is on).">
                      {deliverBusy === 'send' ? <Spinner /> : <StepNum n={5} />}
                      <FlapText>{deliverBusy === 'send' ? 'Sending…' : 'Approve & send'}</FlapText>
                    </button>
                  </div>
                  {/* Phase 5 item 6: a long poll (~min) reassurance — the work continues server-side. */}
                  {(pullBusy || interpBusy || previewBusy || deliverBusy !== null) && (
                    <p className="text-[11px] text-ink-2">Safe to leave this page — it keeps running.</p>
                  )}
                  {/* Reset is a redo, not a pipeline step — set apart + low-emphasis. */}
                  <div>
                    <button onClick={regenerate} disabled={resetBusy || deliverBusy !== null} className="text-xs text-ink-2 underline underline-offset-2 hover:text-ink disabled:opacity-40" title="Reset an approved/sent report back to draft so you can pull fresh data and regenerate it (e.g. the real Q3 report after a baseline test-send). Does not recall delivered email.">
                      {resetBusy ? 'Resetting…' : 'Reset to draft'}
                    </button>
                  </div>
                  {pullErr && <p className="mt-1 text-xs text-danger">{pullErr}</p>}
                  {interpErr && <p className="mt-1 text-xs text-danger">{interpErr}</p>}
                  {previewErr && <p className="mt-1 text-xs text-danger">{previewErr}</p>}
                  {deliverErr && <p className="mt-1 text-xs text-danger">{deliverErr}</p>}
                  {deliverMsg && <p className="mt-1 text-xs text-success">{deliverMsg}</p>}
                  {snapshot && (
                    <div className="mt-2 space-y-1">
                      {/* Per-source status — a failed source is obvious at a glance. */}
                      <div className="flex flex-wrap items-center gap-1">
                        {(snapshot.sources ?? []).map((s) => (
                          <StatusChip key={s.source} tone={sourceTone(s.status)}>
                            {s.source}: {s.status}{s.note ? ` — ${s.note}` : ''}
                          </StatusChip>
                        ))}
                        {(snapshot.sources ?? []).length === 0 && <span className="text-xs text-ink-2">no source statuses</span>}
                      </div>
                      {/* Raw snapshot — internal verify view (not the client report). */}
                      <pre className="max-h-80 overflow-auto rounded border border-line bg-paper-2 p-2 text-[11px] leading-tight">
                        {JSON.stringify(snapshot, null, 2)}
                      </pre>
                    </div>
                  )}

                  {/* 6b-2 verify: the advisory narrative, rendered readably to eyeball quality. */}
                  {narrative && (
                    <div className="mt-3 space-y-2 rounded border border-line bg-paper-2 p-2">
                      <p className="text-sm font-semibold text-ink">{narrative.headline}</p>
                      <p className="text-xs text-ink-2">{narrative.summary}</p>
                      <ol className="space-y-1.5">
                        {narrative.sections.map((s, i) => (
                          <li key={`${s.key}-${i}`} className="rounded border border-line bg-paper p-2">
                            <div className="text-xs font-medium text-ink">
                              {s.heading} <span className="text-ink-2">· {s.key}</span>
                            </div>
                            <div className="text-xs text-ink">{s.metric}</div>
                            {s.whatItMeans && <div className="mt-0.5 text-xs text-ink-2"><b>What it means:</b> {s.whatItMeans}</div>}
                            {s.worthWatching && <div className="mt-0.5 text-xs text-ink-2"><b>Worth watching:</b> {s.worthWatching}</div>}
                          </li>
                        ))}
                      </ol>
                      {narrative.dataNotes.length > 0 && (
                        <div className="text-[11px] text-ink-2">
                          <b>Data notes:</b>
                          <ul className="ml-4 list-disc">
                            {narrative.dataNotes.map((n, i) => <li key={i}>{n}</li>)}
                          </ul>
                        </div>
                      )}
                      <p className="text-xs text-ink"><b>Next focus:</b> {narrative.nextFocus}</p>
                    </div>
                  )}

                  {/* 6b-3 verify: the branded HTML report, in a sandboxed iframe (isolated
                      email styles) — eyeball the design before any send (6b-4). */}
                  {reportHtml && (
                    <div className="mt-3">
                      <p className="mb-1 text-[11px] text-ink-2">Branded report preview (email-first HTML — not sent):</p>
                      <iframe
                        title="Report preview"
                        sandbox=""
                        srcDoc={reportHtml}
                        className="h-[560px] w-full rounded border border-line bg-paper"
                      />
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap items-end gap-2">
                  <Field label="Plausible domain">
                    <input value={plausible} onChange={(e) => setPlausible(e.target.value)} placeholder="cooperfilms.ca" spellCheck={false} className={`${inputCls} font-mono`} />
                  </Field>
                  <Field label="GSC property">
                    <input value={gsc} onChange={(e) => setGsc(e.target.value)} placeholder="sc-domain:cooperfilms.ca" spellCheck={false} className={`${inputCls} font-mono`} />
                  </Field>
                  <Field label="CF Web Analytics ref">
                    <input value={cfRef} onChange={(e) => setCfRef(e.target.value)} placeholder="(optional)" spellCheck={false} className={`${inputCls} font-mono`} />
                  </Field>
                  <Field label="Timezone">
                    <select value={tz} onChange={(e) => setTz(e.target.value)} className={`${inputCls} font-mono`}>
                      <option value="">Select a timezone…</option>
                      {tzList.map((z) => (
                        <option key={z} value={z}>{z}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Report recipient">
                    <input value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="client@example.com" spellCheck={false} type="email" className={`${inputCls} font-mono`} />
                  </Field>
                  <Field label="Client type">
                    <select value={clientType ?? ''} onChange={(e) => setClientType((e.target.value || null) as ClientType)} className={inputCls}>
                      <option value="">—</option>
                      <option value="video">Video</option>
                      <option value="photo">Photo</option>
                    </select>
                  </Field>
                  <label className="flex items-center gap-1 text-xs text-ink-2" title="Include this client in the quarterly report cycle">
                    <input type="checkbox" checked={aEnabled} onChange={(e) => setAEnabled(e.target.checked)} />
                    In quarterly cycle
                  </label>
                  <button onClick={saveAnalytics} disabled={aBusy} className={btnGhost}>Save</button>
                </div>
                {aMsg && <p className="text-xs text-ink-2">{aMsg}</p>}

                {/* 6a-2: the guided "Analytics go-live" checklist — the manual Plausible/GSC steps
                    the Business plan can't API-automate. Completing them confirms the manifest
                    (the reporting engine's input list). Checkboxes are the source of truth; a
                    "detected" hint shows where a manifest field already backs the step. Ticks are
                    persisted by the manifest Save above. */}
                {(() => {
                  const items = goLiveItems(clientType);
                  const { done, total } = goLiveProgress(golive, clientType);
                  const complete = goLiveComplete(golive, clientType);
                  const detected = (v?: 'plausible' | 'gsc' | 'timezone') =>
                    (v === 'plausible' && plausible.trim()) || (v === 'gsc' && gsc.trim()) || (v === 'timezone' && tz.trim());
                  return (
                    <div className="mt-2 border-t border-line pt-2">
                      <div className="mb-1 flex items-center gap-2">
                        <span className="text-xs font-medium text-ink">Analytics go-live</span>
                        <StatusChip tone={complete ? 'success' : 'neutral'}>
                          {complete ? 'Go-live complete' : `${done}/${total} done`}
                        </StatusChip>
                        <span className="text-[11px] text-ink-2">Tick each step, then Save above.</span>
                      </div>
                      <ul className="space-y-1.5">
                        {items.map((item) => (
                          <li key={item.key} className="flex items-start gap-2">
                            <input
                              type="checkbox"
                              className="mt-0.5"
                              checked={golive[item.key] === true}
                              onChange={(e) => setGolive((g) => ({ ...g, [item.key]: e.target.checked }))}
                            />
                            <div className="min-w-0">
                              <div className="text-xs text-ink">
                                {item.label}
                                {detected(item.verify) && <span className="ml-1 inline-flex items-center gap-1 text-[10px] text-success"><Icon name="check" /> detected in manifest</span>}
                              </div>
                              <div className="text-[11px] leading-snug text-ink-2">{item.help}</div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })()}
              </>
            )}
          </div>
        )}
      </details>

      {/* RT-1: rank-tracker config (client_keywords) — the target keyword set + the location/language
          a SERP position is measured at. Same lazy-load/service_role-write pattern as analytics. */}
      <details
        className="mt-2 rounded border border-line"
        open={kOpen}
        onToggle={(e) => {
          const open = (e.target as HTMLDetailsElement).open;
          setKOpen(open);
          if (open && !kLoaded && !kBusy) void loadKeywords();
        }}
      >
        <summary className="cursor-pointer px-2 py-1 text-xs text-ink-2">Rank tracker (target keywords)</summary>
        {kOpen && (
          <div className="space-y-2 p-2 pt-1">
            {!kLoaded && kBusy ? (
              <p className="text-xs text-ink-2">Loading…</p>
            ) : (
              <>
                <Field label="Target keywords (one per line)">
                  <textarea
                    value={kwText}
                    onChange={(e) => setKwText(e.target.value)}
                    placeholder={'toronto video producer\ncorporate video toronto'}
                    spellCheck={false}
                    rows={5}
                    className={`${inputCls} w-72 font-mono`}
                  />
                </Field>
                <div className="flex flex-wrap items-end gap-2">
                  <Field label="Location (required)">
                    <input
                      value={kLocation}
                      onChange={(e) => setKLocation(e.target.value)}
                      list={`dfs-loc-${client.id}`}
                      placeholder="Toronto,Ontario,Canada"
                      spellCheck={false}
                      className={`${inputCls} font-mono`}
                    />
                    <datalist id={`dfs-loc-${client.id}`}>
                      {DFS_LOCATIONS.map((loc) => (
                        <option key={loc} value={loc} />
                      ))}
                    </datalist>
                  </Field>
                  <Field label="Language (required)">
                    <input value={kLanguage} onChange={(e) => setKLanguage(e.target.value)} placeholder="English" spellCheck={false} className={inputCls} />
                  </Field>
                  <label className="flex items-center gap-1 text-xs text-ink-2" title="Include this client in the monthly rank-tracking cron (RT-2)">
                    <input type="checkbox" checked={kEnabled} onChange={(e) => setKEnabled(e.target.checked)} />
                    In monthly rank cycle
                  </label>
                  <button onClick={saveKeywords} disabled={kBusy} className={btnGhost}>Save</button>
                </div>
                <p className="text-[11px] text-ink-2">
                  {kwText.split(/[\n,]/).map((s) => s.trim()).filter(Boolean).length} keyword(s). Location + language are mandatory to enable (a SERP position is location-specific). Pulled monthly, shown in the quarterly report.
                </p>
                {kMsg && <p className="text-xs text-ink-2">{kMsg}</p>}
              </>
            )}
          </div>
        )}
      </details>
    </div>
  );
}

/** 6b-4a — the fleet-level report COPY-ME setting (§14.8): BCC Alchmy on every client report while
 *  the rubric proves out. Reads/writes app_settings via the admin endpoint (service_role). */
function ReportCopySettings() {
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [address, setAddress] = useState('hello@alchmy.studio');
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const r = await callApi<{ copyEnabled: boolean; copyAddress: string | null }>('app-settings', {
          method: 'POST',
          body: JSON.stringify({ get: true }),
        });
        if (!live) return;
        setEnabled(r.copyEnabled);
        setAddress(r.copyAddress ?? 'hello@alchmy.studio');
        setLoaded(true);
      } catch (e) {
        if (live) setMsg(friendlyError(e, (e as Error).message));
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  async function save() {
    setBusy(true);
    setMsg(null);
    try {
      const r = await callApi<{ copyEnabled: boolean; copyAddress: string | null }>('app-settings', {
        method: 'POST',
        body: JSON.stringify({ copyEnabled: enabled, copyAddress: address }),
      });
      setEnabled(r.copyEnabled);
      setAddress(r.copyAddress ?? 'hello@alchmy.studio');
      setMsg('Saved.');
    } catch (e) {
      setMsg(friendlyError(e, (e as Error).message));
    } finally {
      setBusy(false);
    }
  }

  // 6b-4b: run the quarterly cycle NOW (the scheduler runs this on a cron; this is the manual
  // trigger to prove it — fans out pull→interpret→draft per enabled client, never sends).
  const [cycleBusy, setCycleBusy] = useState(false);
  const [force, setForce] = useState(false);
  const [cycleMsg, setCycleMsg] = useState<string | null>(null);
  async function runCycle() {
    setCycleBusy(true);
    setCycleMsg(null);
    try {
      const r = await callApi<{ result: { period: string; dispatched: number; total: number; skipped?: boolean; reason?: string } }>('analytics-report-cycle', {
        method: 'POST',
        body: JSON.stringify({ force }),
      });
      const res = r.result;
      setCycleMsg(res.skipped ? `Skipped ${res.period}: ${res.reason ?? 'already ran'}` : `Queued ${res.dispatched}/${res.total} client report(s) for ${res.period} — drafts will appear shortly (no send).`);
    } catch (e) {
      setCycleMsg(friendlyError(e, (e as Error).message));
    } finally {
      setCycleBusy(false);
    }
  }

  // RT-2: run the monthly rank cycle NOW (the scheduler runs this on a cron; this is the manual
  // trigger to prove it — fans out one rank sample per enabled client, appending rank_snapshots).
  const [rankBusy, setRankBusy] = useState(false);
  const [rankForce, setRankForce] = useState(false);
  const [rankMsg, setRankMsg] = useState<string | null>(null);
  async function runRank() {
    setRankBusy(true);
    setRankMsg(null);
    try {
      const r = await callApi<{ result: { period: string; dispatched: number; total: number; skipped?: boolean; reason?: string } }>('rank-cycle', {
        method: 'POST',
        body: JSON.stringify({ force: rankForce }),
      });
      const res = r.result;
      setRankMsg(res.skipped ? `Skipped ${res.period}: ${res.reason ?? 'already ran'}` : `Queued ${res.dispatched}/${res.total} client rank sample(s) for ${res.period} — snapshots will appear shortly.`);
    } catch (e) {
      setRankMsg(friendlyError(e, (e as Error).message));
    } finally {
      setRankBusy(false);
    }
  }

  return (
    <div className="flex flex-wrap items-end gap-3 rounded border border-line bg-paper-2 p-3">
      {/* Phase 5 item 1: label the fleet-wide block so the fleet-vs-per-client split reads. */}
      <div className="w-full">
        <span className="text-xs font-semibold uppercase tracking-wide text-ink">Fleet reporting</span>
        <span className="ml-2 text-[11px] text-ink-2">settings &amp; manual triggers for the whole fleet</span>
      </div>
      <div className="min-w-[12rem]">
        <div className="text-xs font-medium text-ink">Report delivery</div>
        <div className="text-[11px] text-ink-2">Copy Alchmy on every client report (QA while proving the rubric).</div>
      </div>
      <label className="flex items-center gap-1 text-xs text-ink-2" title="BCC the copy address on every report send">
        <input type="checkbox" checked={enabled} disabled={!loaded || busy} onChange={(e) => setEnabled(e.target.checked)} />
        Copy me on every report
      </label>
      <Field label="Copy-to address">
        <input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="hello@alchmy.studio" spellCheck={false} type="email" className={`${inputCls} font-mono`} />
      </Field>
      <button onClick={save} disabled={!loaded || busy} className={btnGhost}>
        {busy ? 'Saving…' : 'Save'}
      </button>
      {msg && <span className="text-xs text-ink-2">{msg}</span>}

      {/* 6b-4b: manual trigger for the quarterly scheduler (prove it without waiting a quarter). */}
      <div className="flex w-full flex-wrap items-center gap-2 border-t border-line pt-2">
        <span className="text-[11px] text-ink-2">Quarterly cycle (scheduler runs this automatically):</span>
        <label className="flex items-center gap-1 text-xs text-ink-2" title="Re-run even if the cycle already ran for this quarter">
          <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} />
          force
        </label>
        <button onClick={runCycle} disabled={cycleBusy} className={btnGhost} title="Run pull → interpret for every enabled client with a recipient, leaving drafts (no send)">
          {cycleBusy ? 'Running…' : 'Run quarterly cycle now'}
        </button>
        {cycleMsg && <span className="text-xs text-ink-2">{cycleMsg}</span>}
      </div>

      {/* RT-2: manual trigger for the MONTHLY rank scheduler (prove it without waiting for the 1st). */}
      <div className="flex w-full flex-wrap items-center gap-2 border-t border-line pt-2">
        <span className="text-[11px] text-ink-2">Monthly rank cycle (scheduler runs this automatically):</span>
        <label className="flex items-center gap-1 text-xs text-ink-2" title="Re-run even if the rank cycle already ran for this month">
          <input type="checkbox" checked={rankForce} onChange={(e) => setRankForce(e.target.checked)} />
          force
        </label>
        <button onClick={runRank} disabled={rankBusy} className={btnGhost} title="Sample each target keyword's Google position for every enabled client, appending rank snapshots">
          {rankBusy ? 'Running…' : 'Run monthly rank cycle now'}
        </button>
        {rankMsg && <span className="text-xs text-ink-2">{rankMsg}</span>}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-xs text-ink-2">
      {label}
      {children}
    </label>
  );
}

// Phase 5 item 2: the numbered badge that turns the report-action row into a 1→5 stepper.
// border-current inherits the button's text colour (ink on ghost, paper on the solid primary).
function StepNum({ n }: { n: number }) {
  return (
    <span className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-current text-[10px] font-semibold leading-none opacity-60">
      {n}
    </span>
  );
}

function Pill({ ok, children }: { ok?: boolean; children: React.ReactNode }) {
  return <StatusChip tone={ok ? 'success' : 'warning'}>{children}</StatusChip>;
}
